import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Building2, Plus, Edit, Trash2, Search, Clock } from "lucide-react";
import { toast } from "sonner";

export default function DepartamentosPage() {
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    horario_inicio: "",
    horario_fin: ""
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchDepartamentos();
  }, []);

  const fetchDepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/departamentos`, getAuthHeaders());
      setDepartamentos(res.data);
    } catch (err) {
      toast.error("Error al cargar departamentos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) {
        await axios.put(`${API}/departamentos/${editingDept.id}`, formData, getAuthHeaders());
        toast.success("Departamento actualizado");
      } else {
        await axios.post(`${API}/departamentos`, formData, getAuthHeaders());
        toast.success("Departamento creado");
      }
      setShowDialog(false);
      setFormData({ name: "", description: "", horario_inicio: "", horario_fin: "" });
      setEditingDept(null);
      fetchDepartamentos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description || "",
      horario_inicio: dept.horario_inicio || "",
      horario_fin: dept.horario_fin || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (deptId) => {
    if (!confirm("¿Eliminar este departamento?")) return;
    try {
      await axios.delete(`${API}/departamentos/${deptId}`, getAuthHeaders());
      toast.success("Departamento eliminado");
      fetchDepartamentos();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filteredDepartamentos = departamentos.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (dept.description && dept.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Departamentos
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona los departamentos de la organización
            </p>
          </div>
          <Button
            onClick={() => {
              setEditingDept(null);
              setFormData({ name: "", description: "", horario_inicio: "", horario_fin: "" });
              setShowDialog(true);
            }}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Departamento
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Buscar departamentos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Cards Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Cargando...</p>
          </div>
        ) : filteredDepartamentos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchQuery ? "No se encontraron departamentos" : "No hay departamentos creados"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDepartamentos.map((dept) => (
              <Card key={dept.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#45E69A]/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#45E69A]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{dept.name}</CardTitle>
                        {dept.description && (
                          <p className="text-sm text-slate-500 mt-1">{dept.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {dept.horario_inicio && dept.horario_fin && (
                    <div className="flex items-center gap-2 mb-3 text-sm text-slate-600 dark:text-slate-300" data-testid={`dept-horario-${dept.id}`}>
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>Horario: {dept.horario_inicio} - {dept.horario_fin}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(dept)}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(dept.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDept ? "Editar Departamento" : "Nuevo Departamento"}
              </DialogTitle>
              <DialogDescription>
                {editingDept
                  ? "Modifica la información del departamento"
                  : "Crea un nuevo departamento para la organización"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Recursos Humanos"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Gestión de personal y nómina"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horario de atención <span className="text-xs text-slate-400 font-normal">(opcional — usado por WhatsApp para "fuera de horario"; vacío = usa el horario de la línea)</span></Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="horario_inicio" className="text-xs text-slate-500">Desde</Label>
                      <Input
                        id="horario_inicio"
                        type="time"
                        value={formData.horario_inicio}
                        onChange={(e) => setFormData({ ...formData, horario_inicio: e.target.value })}
                        data-testid="dept-horario-inicio"
                      />
                    </div>
                    <div>
                      <Label htmlFor="horario_fin" className="text-xs text-slate-500">Hasta</Label>
                      <Input
                        id="horario_fin"
                        type="time"
                        value={formData.horario_fin}
                        onChange={(e) => setFormData({ ...formData, horario_fin: e.target.value })}
                        data-testid="dept-horario-fin"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                  {editingDept ? "Guardar Cambios" : "Crear Departamento"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
